import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { TextFieldElement, TextFieldElementProps } from 'react-hook-form-mui';

import { useQuery } from '@tanstack/react-query';

import { AutocompleteElement } from 'commonComponents/AutocompleteElement';
import { buildHexMaskInput } from 'commonComponents/HexMaskInput';

import { loadUpgradeSystemIds } from 'api/licenses';
import { hasInficryptSystemId, hasSystemId } from 'licenses/utils';

const SYSTEMID_REGEX = /^(?:[A-F0-9]{2}:){19}[A-F0-9]{2}(?:[:;][A-F0-9]{2})?;?$|^[A-F0-9]{40}$|^[A-F0-9]{42}$/i;
const InputMask = buildHexMaskInput('00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00[;00]');

const INFICRYPT_SYSTEMID_REGEX = /^[A-Fa-f0-9]{16}$/;
const InficryptInputMask = buildHexMaskInput('0000000000000000');

function validateInficryptSystemId(hexString: string) {
    const firstPart = hexString.slice(0, 14);
    const actualChecksum = parseInt(hexString.slice(14), 16);

    // Convert hex string to byte array
    const byteArray: Array<number> = [];
    for (let i = 0; i < firstPart.length; i += 2) {
        byteArray.push(parseInt(firstPart.slice(i, i + 2), 16));
    }

    // Calculate checksum
    let c = 0;
    for (const b of byteArray) {
        c += b;
        c += b * b * b;
    }
    const expectedChecksum = c & 0xff;
    if (actualChecksum === expectedChecksum) {
        return true;
    }
    return 'Invalid checksum';
}

function InficryptSystemIdField({ defaultProps }: { defaultProps: Partial<TextFieldElementProps> }) {
    return (
        <SystemIdField
            defaultProps={defaultProps}
            pattern={INFICRYPT_SYSTEMID_REGEX}
            inputComponent={InficryptInputMask}
            validate={validateInficryptSystemId}
        />
    );
}

function SystemIdField({
    readOnly = false,
    defaultProps,
    validate,
    pattern = SYSTEMID_REGEX,
    inputComponent = InputMask,
}: {
    readOnly?: boolean;
    defaultProps: Partial<TextFieldElementProps>;
    pattern?: RegExp;
    validate?: TextFieldElementProps['rules']['validate'];
    inputComponent?: typeof InputMask;
}) {
    return (
        <TextFieldElement
            label="System ID"
            name="system_id"
            required
            {...defaultProps}
            data-testid="LicenseFileForm_SystemId"
            slotProps={{
                input: { inputComponent, disabled: readOnly },
                inputLabel: { shrink: true },
            }}
            rules={{ pattern, validate }}
            parseError={error => {
                if (error.message) {
                    return error.message;
                }
                if (error.type === 'pattern') {
                    return 'Invalid System ID';
                }
                return 'Unknown error';
            }}
        />
    );
}

function SystemIdSelect({ systemIds }) {
    return <AutocompleteElement label="System ID" name="system_id" required options={systemIds} disablePortal />;
}

function SystemId({ license, defaultProps }) {
    const { setValue } = useFormContext();

    const { data: validSystemIds } = useQuery({
        queryKey: ['SystemId', 'list', { licenseId: license.id, licenseConfiguration: license.configuration }],
        queryFn: () => {
            if (hasSystemId(license.configuration)) {
                return loadUpgradeSystemIds(license.id);
            }
            return [];
        },
        meta: { errorMessage: 'Loading valid system IDs failed.' },
    });

    function determineDisplayMode(validSystemIds) {
        if (validSystemIds == null) {
            return undefined;
        }
        if (validSystemIds.length === 1) {
            return 'fixed';
        }
        if (validSystemIds.length > 1) {
            return 'options';
        }
        return 'freeform';
    }

    const displayMode = determineDisplayMode(validSystemIds);

    useEffect(() => {
        const num = validSystemIds?.length ?? 0;
        if (num === 1) {
            // only one option, force this option
            setValue('system_id', validSystemIds[0], { shouldValidate: true, shouldDirty: true });
            return;
        }
    }, [setValue, validSystemIds]);

    switch (displayMode) {
        case 'freeform':
            return <SystemIdField defaultProps={defaultProps} />;
        case 'options':
            return <SystemIdSelect systemIds={validSystemIds} />;
        case 'fixed':
            return <SystemIdField defaultProps={defaultProps} readOnly />;
        default:
            return null;
    }
}

export function SystemIdOrInficryptSystemId({ license, defaultProps }) {
    const { register } = useFormContext();
    if (hasInficryptSystemId(license.configuration)) {
        return <InficryptSystemIdField defaultProps={defaultProps} />;
    }
    if (hasSystemId(license.configuration)) {
        return <SystemId license={license} defaultProps={defaultProps} />;
    }

    return <input type="hidden" {...register('system_id')}></input>;
}
